import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Grid,
  MenuItem,
  Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { addDocument, getCollection, updateDocument, deleteDocument } from '../../services/firestoreService';
import { useBranch } from '../../context/BranchContext';

// Tipos de convenios disponibles
const TIPOS_CONVENIO = [
  'Comunidad UDLA',
  'Público',
  'Privado',
  'Empresarial',
  'Otro'
];

// Convenios predefinidos comunes en Chile
const CONVENIOS_PREDEFINIDOS = [
  { name: 'Estudiante UDLA', type: 'Comunidad UDLA', description: 'Convenio para estudiantes de la Universidad de Las Américas' },
  { name: 'Funcionario UDLA', type: 'Comunidad UDLA', description: 'Convenio para funcionarios de la Universidad de Las Américas' },
  { name: 'Fonasa', type: 'Público', description: 'Fondo Nacional de Salud' },
  { name: 'Isapre Banmédica', type: 'Privado', description: 'Isapre privada' },
  { name: 'Isapre Colmena', type: 'Privado', description: 'Isapre privada' },
  { name: 'Isapre Consalud', type: 'Privado', description: 'Isapre privada' },
  { name: 'Isapre Cruz Blanca', type: 'Privado', description: 'Isapre privada' },
  { name: 'Isapre Vida Tres', type: 'Privado', description: 'Isapre privada' },
  { name: 'Isapre Nueva Masvida', type: 'Privado', description: 'Isapre privada' },
  { name: 'Particular', type: 'Privado', description: 'Sin convenio' },
  { name: 'Seguro Complementario', type: 'Privado', description: 'Seguro adicional' }
];

const ManageConvenios = () => {
  const { isAdmin, activeBranchId, accessibleBranches, filterByBranch } = useBranch();
  const [convenioName, setConvenioName] = useState('');
  const [convenioType, setConvenioType] = useState('');
  const [description, setDescription] = useState('');
  const [convenios, setConvenios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editingConvenio, setEditingConvenio] = useState(null);
  const [tiposConvenios, setTiposConvenios] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [tiposPrestacion, setTiposPrestacion] = useState([]);
  const [aranceles, setAranceles] = useState([]);
  const [discountByTipo, setDiscountByTipo] = useState({});
  const [exceptions, setExceptions] = useState([]);
  const [selectedArancel, setSelectedArancel] = useState(null);
  const [exceptionPct, setExceptionPct] = useState('');

  useEffect(() => {
    fetchConvenios();
    fetchCatalogs();
    setBranches(accessibleBranches);
  }, [accessibleBranches]);

  const fetchConvenios = async () => {
    try {
      const data = isAdmin
        ? await getCollection('convenios')
        : (await getDocs(
            query(collection(db, 'convenios'), where('branchIds', 'array-contains', activeBranchId))
          )).docs.map((d) => ({ id: d.id, ...d.data() }));
      setConvenios(data);

      // Extraer tipos únicos para el desplegable
      const tiposUnicos = [...new Set(data.map(c => c.name).filter(Boolean))];
      setTiposConvenios(tiposUnicos);
    } catch (error) {
      console.error('Error fetching convenios:', error);
    }
  };

  const fetchCatalogs = async () => {
    try {
      const [tiposData, arancelesData] = await Promise.all([
        getCollection('tiposPrestacion'),
        getCollection('aranceles')
      ]);
      setTiposPrestacion(tiposData.filter((t) => t.activo !== false).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      const arancelesFiltered = isAdmin ? arancelesData : filterByBranch(arancelesData);
      setAranceles(arancelesFiltered.filter((a) => a.activo !== false).sort((a, b) => (a.prestacion || '').localeCompare(b.prestacion || '', 'es')));
    } catch (error) {
      console.error('Error fetching catalogs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const discountExceptions = Object.fromEntries(
        exceptions
          .filter((ex) => ex.arancelId && ex.discountPct !== '')
          .map((ex) => [ex.arancelId, Number(ex.discountPct)])
      );
      const selectedBranchIds = selectedBranches.map((b) => b.id);
      const convenioData = {
        name: convenioName,
        type: convenioType,
        description: description,
        branchIds: isAdmin ? selectedBranchIds : (selectedBranchIds.length ? selectedBranchIds : [activeBranchId]),
        discountByTipoPrestacion: discountByTipo,
        discountExceptions,
        isActive: true,
        createdAt: editingConvenio ? editingConvenio.createdAt : new Date(),
        updatedAt: new Date()
      };

      if (editingConvenio) {
        await updateDocument('convenios', editingConvenio.id, convenioData);
        setSnackbar({
          open: true,
          message: 'Convenio actualizado exitosamente',
          severity: 'success'
        });
        setEditingConvenio(null);
      } else {
        await addDocument('convenios', convenioData);
        setSnackbar({
          open: true,
          message: 'Convenio creado exitosamente',
          severity: 'success'
        });
      }

      // Limpiar formulario
      setConvenioName('');
      setConvenioType('');
      setDescription('');
      setSelectedBranches([]);

      // Actualizar lista
      fetchConvenios();
    } catch (error) {
      console.error('Error saving convenio:', error);
      setSnackbar({
        open: true,
        message: 'Error al guardar el convenio',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (convenio) => {
    setEditingConvenio(convenio);
    setConvenioName(convenio.name);
    setConvenioType(convenio.type);
    setDescription(convenio.description || '');
    setSelectedBranches(branches.filter((b) => (convenio.branchIds || []).includes(b.id)));
    setDiscountByTipo(convenio.discountByTipoPrestacion || {});
    const exEntries = Object.entries(convenio.discountExceptions || {});
    setExceptions(
      exEntries
        .map(([arancelId, pct]) => {
          const arancel = aranceles.find((a) => a.id === arancelId);
          return { arancelId, arancelName: arancel?.prestacion || arancelId, discountPct: Number(pct) };
        })
        .filter((ex) => !isNaN(ex.discountPct))
    );
  };

  const handleCancelEdit = () => {
    setEditingConvenio(null);
    setConvenioName('');
    setConvenioType('');
    setDescription('');
    setSelectedBranches([]);
    setDiscountByTipo({});
    setExceptions([]);
    setSelectedArancel(null);
    setExceptionPct('');
  };

  const handlePctChange = (tipoName, value) => {
    const pct = Math.max(0, Math.min(100, Number(value) || 0));
    setDiscountByTipo((prev) => ({ ...prev, [tipoName]: pct }));
  };

  const handleAddException = () => {
    if (!selectedArancel || exceptionPct === '') return;
    const pct = Math.max(0, Math.min(100, Number(exceptionPct) || 0));
    setExceptions((prev) => [
      ...prev.filter((ex) => ex.arancelId !== selectedArancel.id),
      { arancelId: selectedArancel.id, arancelName: selectedArancel.prestacion, discountPct: pct }
    ]);
    setSelectedArancel(null);
    setExceptionPct('');
  };

  const handleRemoveException = (arancelId) => {
    setExceptions((prev) => prev.filter((ex) => ex.arancelId !== arancelId));
  };

  const handleDelete = async (convenioId) => {
    if (window.confirm('¿Estás seguro de eliminar este convenio?')) {
      try {
        await deleteDocument('convenios', convenioId);
        setSnackbar({
          open: true,
          message: 'Convenio eliminado exitosamente',
          severity: 'success'
        });
        fetchConvenios();
      } catch (error) {
        console.error('Error deleting convenio:', error);
        setSnackbar({
          open: true,
          message: 'Error al eliminar el convenio',
          severity: 'error'
        });
      }
    }
  };

  const handleToggleActive = async (convenio) => {
    try {
      await updateDocument('convenios', convenio.id, {
        isActive: !convenio.isActive,
        updatedAt: new Date()
      });
      setSnackbar({
        open: true,
        message: `Convenio ${!convenio.isActive ? 'activado' : 'desactivado'} exitosamente`,
        severity: 'success'
      });
      fetchConvenios();
    } catch (error) {
      console.error('Error toggling convenio:', error);
      setSnackbar({
        open: true,
        message: 'Error al actualizar el convenio',
        severity: 'error'
      });
    }
  };

  const handleLoadPredefined = async () => {
    setLoading(true);
    try {
      let count = 0;
      for (const convenio of CONVENIOS_PREDEFINIDOS) {
        // Verificar si ya existe
        const exists = convenios.some(c => c.name === convenio.name);
        if (!exists) {
          await addDocument('convenios', {
            ...convenio,
            branchIds: isAdmin ? [] : [activeBranchId],
            isActive: true,
            createdAt: new Date(),
            updatedAt: new Date()
          });
          count++;
        }
      }
      
      setSnackbar({
        open: true,
        message: `${count} convenios predefinidos cargados exitosamente`,
        severity: 'success'
      });
      
      fetchConvenios();
    } catch (error) {
      console.error('Error loading predefined convenios:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar convenios predefinidos',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Gestión de Convenios
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Administra los convenios disponibles (Comunidad UDLA, Fonasa, Isapres, etc.)
      </Typography>
      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
        <strong>Importante:</strong> Los convenios de tipo <strong>"Comunidad UDLA"</strong> aparecerán en el formulario público de solicitud (/solicitud-udla)
      </Alert>
      {/* Formulario */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          {editingConvenio ? 'Editar Convenio' : 'Agregar Nuevo Convenio'}
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Nombre del Convenio"
                value={convenioName}
                onChange={(e) => setConvenioName(e.target.value)}
                required
                placeholder="Ej: Fonasa, Isapre Banmédica"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                select
                label="Tipo"
                value={convenioType}
                onChange={(e) => setConvenioType(e.target.value)}
                required
                helperText="Selecciona el tipo de convenio"
              >
                <MenuItem value="">Seleccionar tipo...</MenuItem>
                {tiposConvenios.map((tipo) => (
                  <MenuItem key={tipo} value={tipo}>
                    {tipo}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={2}
                placeholder="Descripción opcional del convenio"
              />
            </Grid>
            <Grid size={12}>
              <Autocomplete
                multiple
                options={branches}
                value={selectedBranches}
                onChange={(e, value) => setSelectedBranches(value)}
                getOptionLabel={(b) => b.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sucursales (opcional)"
                    helperText="Sucursales donde aplica este convenio. Vacío = todas."
                  />
                )}
                noOptionsText="Sin sucursales configuradas"
              />
            </Grid>
            <Grid size={12}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Descuentos por Tipo de Prestación</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo de Prestación</TableCell>
                        <TableCell align="right" width="120">Descuento %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tiposPrestacion.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell>{t.name}</TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={discountByTipo[t.name] ?? ''}
                              onChange={(e) => handlePctChange(t.name, e.target.value)}
                              inputProps={{ min: 0, max: 100 }}
                              sx={{ width: 80 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {tiposPrestacion.length === 0 && (
                        <TableRow><TableCell colSpan={2}>Sin tipos de prestación configurados</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="subtitle1" gutterBottom>Excepciones por Prestación Específica</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                  <Autocomplete
                    options={aranceles}
                    value={selectedArancel}
                    onChange={(_, v) => setSelectedArancel(v)}
                    getOptionLabel={(a) => a.prestacion || ''}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={(params) => <TextField {...params} label="Prestación" size="small" />}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    type="number"
                    size="small"
                    label="Desc. %"
                    value={exceptionPct}
                    onChange={(e) => setExceptionPct(e.target.value)}
                    inputProps={{ min: 0, max: 100 }}
                    sx={{ width: 100 }}
                  />
                  <Button variant="outlined" size="small" onClick={handleAddException}>Agregar</Button>
                </Box>
                {exceptions.length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Prestación</TableCell>
                          <TableCell align="right" width="120">Desc. %</TableCell>
                          <TableCell align="right" width="60">Acción</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {exceptions.map((ex) => (
                          <TableRow key={ex.arancelId}>
                            <TableCell>{ex.arancelName}</TableCell>
                            <TableCell align="right">{ex.discountPct}%</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" color="error" onClick={() => handleRemoveException(ex.arancelId)}>
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
            <Grid size={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ mr: 1 }}
              >
                {loading ? 'Guardando...' : editingConvenio ? 'Actualizar Convenio' : 'Crear Convenio'}
              </Button>
              {editingConvenio && (
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                >
                  Cancelar
                </Button>
              )}
              {!editingConvenio && convenios.length === 0 && (
                <Button
                  variant="outlined"
                  onClick={handleLoadPredefined}
                  disabled={loading}
                  sx={{ ml: 1 }}
                >
                  Cargar Convenios Predefinidos
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>
      {/* Lista de convenios */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Convenios Configurados ({convenios.length})
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {convenios.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No hay convenios configurados. Haz clic en "Cargar Convenios Predefinidos" para empezar.
                  </TableCell>
                </TableRow>
              ) : (
                convenios
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((convenio) => (
                    <TableRow key={convenio.id}>
                      <TableCell>
                        <strong>{convenio.name}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={convenio.name} 
                          size="small" 
                          color={
                            convenio.type === 'Comunidad UDLA' ? 'secondary' :
                            convenio.type === 'Público' ? 'primary' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{convenio.description || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={convenio.isActive ? 'Activo' : 'Inactivo'}
                          color={convenio.isActive ? 'success' : 'default'}
                          size="small"
                          onClick={() => handleToggleActive(convenio)}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(convenio)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(convenio.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageConvenios;
